<script setup lang="ts">
  import draggable from "vuedraggable";
  import { DraggableItem } from "~/entities/DraggableItem";

  interface Props {
    items: DraggableItem[];
    selectedId?: string | undefined;
    canDrag?: boolean;
    isDragging?: boolean;
  }
  const props = defineProps<Props>();

  interface Emits {
    (event: "update:items", items: DraggableItem[]): void;
    (event: "update:isDragging", isDragging: boolean): void;
  }
  const emit = defineEmits<Emits>();

  // ソート用
  const sortedItems = ref(props.items);
  const sortedItemsModel = computed<DraggableItem[]>({
    get: () => {
      return sortedItems.value;
    },
    set: value => {
      // 先行して表示に反映
      sortedItems.value = value;
      // ソート結果を上位コンポーネントへ通知
      emit("update:items", value);
    },
  });

  const isDraggingNow = ref(false);
  watch(isDraggingNow, value => {
    emit("update:isDragging", value);
  });

  const isTabletOrSp = isTabletAndSpDisplayRef();
  // バッジが常に右端に来るように調整
  const titleWidth = computed(() => (isTabletOrSp.value ? "100%" : props.canDrag ? "80%" : "90%"));
</script>

<template>
  <draggable
    v-model="sortedItemsModel"
    item-key="id"
    handle=".handle"
    @start="isDraggingNow = true"
    @end="isDraggingNow = false"
  >
    <template #item="{ element: item }">
      <div
        class="item_list d-flex align-center"
        :class="{
          item_canview: item.canView,
          item_hasunread: item.hasUnread,
          item_selected: item.id === selectedId,
        }"
        data-testid="draggable-list-item"
        @click="item.onClick(item.id)"
      >
        <AtomsSvgIcon
          v-if="canDrag"
          class="handle my-1 ml-1 mr-2"
          :width="12"
          img-path="assets/images/icon/handle.svg"
        />

        <AtomsUserIcon
          v-if="item.isUserIcon"
          :is-owner="item.isOwner"
          :width="20"
          :height="20"
          :src="item.imgPath"
          class="user-icon mr-2"
        />
        <AtomsSvgIcon v-else :width="20" :height="20" :img-path="item.imgPath" class="mr-2" />

        <div class="item_title justify-start">
          <div>{{ item.label }}</div>
          <v-badge
            v-if="item.hasUnread"
            color="red"
            dot
            class="ml-auto my-auto pr-1"
            :offset-x="-4"
            :offset-y="-4"
            data-testid="draggable-list-item-badge"
          />
        </div>
      </div>
    </template>
  </draggable>
</template>

<style lang="scss" scoped>
  @import "assets/media.scss";

  .item_list {
    padding: 4px 6px;
    margin-top: 15px;
    border-radius: 4px 0 0 4px;
    @include display_sp {
      padding: 12px;
      margin: 0;
      border-radius: 0;
      border-bottom: 1px solid rgba(57, 57, 57, 1);
    }
    cursor: pointer;
    color: #8a8a8a;
    &.item_selected,
    &:hover {
      background-color: #2d2d2d;
    }
    &.item_canview {
      color: white;
    }
    &.item_hasunread {
      font-weight: 700;
    }
    &.item_hasunread .item_title {
      vertical-align: middle;
    }
    .item_title {
      display: flex;
      width: v-bind(titleWidth);
    }
  }
  .handle {
    cursor: move;
  }
  .user-icon {
    // なぜかはみ出すケースがあるので、最大幅を設定
    max-width: 20px;
  }
</style>
